import { useStore } from 'zustand';

import { config } from '@/config/config';
import { buildUrlQueryParams } from '@/utils/url.util';
import { SearchType } from '@/types/most-search-type';
import { SelectedProcedures } from '@/app/(main)/(pages)/_components/search/search.component';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { userLocationStore } from '@/store/user-location.store';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';

interface UseSearchProps {
  locationOverride?: OverridedUserLocation;
}

export function useSearch({ locationOverride }: UseSearchProps = {}) {
  const enabledRedirect = config.stageOneEnabled;
  const userStore = useStore(userLocationStore, (state) => state.userLocation);
  const userLocation = locationOverride ?? userStore;
  const searchParams = useSearchParams(Object.values(PersistedUserNavigationSearchParams));

  const isProcedureSearch = (searchType: SearchType): boolean => {
    return [
      SearchType.IMAGE_EXAM,
      SearchType.LABORATORIAL_EXAM,
      SearchType.ONLINE_CONSULTATION,
      SearchType.PRESENTIAL_CONSULTATION,
      SearchType.VACCINE,
      SearchType.EMERGENCY,
      SearchType.SURGERY,
    ].includes(searchType);
  };

  const checkLaboratorialOrVaccineSearch = (searchType: SearchType): boolean => {
    return [SearchType.LABORATORIAL_EXAM, SearchType.VACCINE].includes(searchType);
  };

  const resolveName = (searchResult: SearchResult): string => {
    if (
      searchResult.searchType === SearchType.DOCTOR
      || searchResult.searchType === SearchType.CLINIC
      || searchResult.searchType === SearchType.BRANCH_LABORATORY
    ) {
      return formatToTitleCase(`${searchResult.displayName}`);
    }
    return `${searchResult.displayName}`;
  };

  const buildExamsUrl = (searchResult: SearchResult): string => {
    const basePath = searchResult.searchType === SearchType.VACCINE ? '/busca/vacina/' : '/busca/exames-laboratoriais/';

    const procedureCodes = searchResult?.code?.split(',').filter(Boolean);
    return buildUrlQueryParams(basePath, {
      exames: procedureCodes || [''],
    });
  };

  const buildRedirectUrlWithCity = (
    path: string,
    city: string | undefined,
    isMultipleSearch: boolean = false,
    otherParams: Record<string, string> = {},
  ): string => {
    const fullPath = `${config.ag.url}${path}`;
    if (!city) {
      return fullPath;
    }

    if (otherParams && Object.keys(otherParams).length > 0) {
      const fullPathWithParams = buildUrlQueryParams(fullPath, {
        ...otherParams,
      });
      const separator = isMultipleSearch ? '&' : '?';

      return `${fullPathWithParams}${separator}cidade=${city}`;
    }

    const separator = isMultipleSearch ? '&' : '?';

    return `${fullPath}${separator}cidade=${city}`;
  };

  const resolveLink = (searchResult: SearchResult, otherParams?: Record<string, string>): string => {
    if (!searchResult.slug && !searchResult.code) {
      return '/';
    }

    const linkMap = {
      [SearchType.DOCTOR]: `/profissionais/${searchResult.slug}/`,
      [SearchType.CLINIC]: `/parceiros/${searchResult.slug}/`,
      [SearchType.BRANCH_LABORATORY]: `/laboratorios/${searchResult.slug}/`,
      [SearchType.LABORATORIAL_EXAM]: buildExamsUrl(searchResult),
      [SearchType.VACCINE]: buildExamsUrl(searchResult),
    };

    const slug = searchResult.slug || searchResult.code;
    const pathUrl = linkMap[searchResult.searchType as keyof typeof linkMap] || `/busca/${slug}/`;

    const shouldRedirect = enabledRedirect && isProcedureSearch(searchResult.searchType as SearchType);
    const isMultipleSearch = checkLaboratorialOrVaccineSearch(searchResult.searchType as SearchType);

    let fullUrl = pathUrl;

    const cityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);
    const city: string | undefined = cityParam ?? userLocation?.city ?? searchResult?.city;

    if (shouldRedirect) {
      fullUrl = buildRedirectUrlWithCity(pathUrl, city, isMultipleSearch, otherParams);
      return buildUrlWithPersistedParams({
        baseUrl: fullUrl,
        overrides: city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined,
      });
    }

    return buildUrlWithPersistedParams({
      baseUrl: pathUrl,
      overrides: city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined,
    });
  };

  const handleSearchExamsUrl = (isVaccine: boolean, selectedProcedures: SelectedProcedures[]): string => {
    const basePath = isVaccine ? '/busca/vacina/' : '/busca/exames-laboratoriais/';
    const pathUrl = buildUrlQueryParams(basePath, {
      exames: selectedProcedures.map((exam) => exam.code),
    });

    const cityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);
    const city: string | undefined = userLocation?.city ?? (cityParam ?? undefined);

    if (enabledRedirect) {
      const base = `${config.ag.url}${pathUrl}`;
      return buildUrlWithPersistedParams({
        baseUrl: base,
        overrides: city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined,
      });
    }

    return buildUrlWithPersistedParams({
      baseUrl: pathUrl,
      overrides: city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined,
    });
  };

  return {
    isProcedureSearch,
    checkLaboratorialOrVaccineSearch,
    resolveName,
    buildExamsUrl,
    resolveLink,
    handleSearchExamsUrl,
    buildRedirectUrlWithCity,
  };
}
